import * as t from 'io-ts';
import {isString} from 'lodash';

import {makeEnumRuntimeType} from '../../../libs/shared/helpers/src/typescript/enumHelpers';
import {isPaletteColor} from '../../../libs/shared/ui/src/styles/colorHelpers';
import {PaletteColorsEnum} from '../../../libs/shared/ui/src/styles/paletteModel';
import {isValidEmoji} from '../../../srcCommon/helpers/emoji/emojiTextHelpers';
import {baseEmojiValidationMap} from '../../services/emojis/emojis';

/*
 * Runtime type.
 */

export enum HighlightTypesEnum {
  NONE = 'NONE',
  EMOJI = 'EMOJI',
  COLOR = 'COLOR',
}

// No highlight.
export const RHighlightNoneBase = t.type({
  type: t.literal(HighlightTypesEnum.NONE),
});

// Emoji highlight.
export const RHighlightEmojiBase = t.type({
  type: t.literal(HighlightTypesEnum.EMOJI),
  highlightEmoji: t.string,
});

// Color highlight.
export const RHighlightColorBase = t.type({
  type: t.literal(HighlightTypesEnum.COLOR),
  highlightColor: makeEnumRuntimeType(PaletteColorsEnum),
});

/*
 * Type.
 */

export interface HighlightNoneBaseModel extends t.TypeOf<typeof RHighlightNoneBase> {}
export interface HighlightEmojiBaseModel extends t.TypeOf<typeof RHighlightEmojiBase> {}
export interface HighlightColorBaseModel extends t.TypeOf<typeof RHighlightColorBase> {}

/** Highlight-capable model base that offers emoji and color support. */
export type HighlightBaseModel = HighlightEmojiBaseModel | HighlightNoneBaseModel | HighlightColorBaseModel;

/*
 * IO methods.
 */

export function parseHighlight(srcHighlight: any): HighlightBaseModel {
  const trimmedHighlight = srcHighlight && isString(srcHighlight) && srcHighlight.trim();

  // If the highlight is an emoji, parse it as such.
  if (isValidEmoji(trimmedHighlight, baseEmojiValidationMap)) {
    return {
      type: HighlightTypesEnum.EMOJI,
      highlightEmoji: trimmedHighlight,
    };
  }

  // If the highlight is a color, parse it as such.
  const highlightColor = parseHighlightColor(srcHighlight);
  if (isPaletteColor(highlightColor)) {
    return {
      type: HighlightTypesEnum.COLOR,
      highlightColor,
    };
  }

  // Otherwise, it has no highlight.
  return {
    type: HighlightTypesEnum.NONE,
  };
}

function parseHighlightColor(src: any) {
  // Rename some legacy colors.
  switch (src) {
    case 'dark-blue':
      return PaletteColorsEnum.BLUE;

    case 'light-blue':
      return PaletteColorsEnum.TEAL;

    default:
      return src;
  }
}
